import { useEffect, useState } from "react";
import { DownloadSimple } from "@phosphor-icons/react";

/**
 * "Install app" button — surfaces the PWA install prompt when the browser
 * fires `beforeinstallprompt` (Android Chrome, desktop Chrome/Edge).
 *
 * iOS Safari never fires this event — Apple requires the user to use
 * Share → "Add to Home Screen". For iOS users we show a one-line hint
 * with the instructions instead.
 *
 * The component hides itself entirely once the app is already running in
 * standalone mode (already installed) so it doesn't clutter the sidebar.
 */
export default function InstallAppButton() {
  const [installEvent, setInstallEvent] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    // Already installed and launched from home screen → hide.
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);
    if (standalone) return undefined;

    // iOS detection (no beforeinstallprompt support).
    const ua = window.navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (isIos) setShowIosHint(true);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => {
      setInstallEvent(null);
      setIsStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (isStandalone) return null;

  // Android / desktop browsers — proper install prompt available
  if (installEvent) {
    return (
      <button
        type="button"
        data-testid="pwa-install-btn"
        onClick={async () => {
          installEvent.prompt();
          await installEvent.userChoice;
          setInstallEvent(null);
        }}
        className="w-full inline-flex items-center justify-center gap-2 rounded-sm px-3 py-2 text-xs label-tracking border border-current/30 hover:bg-current/5 transition-colors text-current"
      >
        <DownloadSimple size={13} weight="bold" />
        Install app
      </button>
    );
  }

  // iOS Safari — quick how-to in two lines
  if (showIosHint) {
    return (
      <div
        data-testid="pwa-ios-hint"
        className="text-[10px] leading-relaxed rounded-sm px-3 py-2 opacity-75 border border-dashed border-current/30 text-current"
      >
        <div className="font-medium mb-0.5">Install on iPhone / iPad</div>
        <div>Tap <span aria-label="share">Share</span> &rarr; &ldquo;Add to Home Screen&rdquo;</div>
      </div>
    );
  }

  return null;
}
